import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import PageHeader from "@/components/common/PageHeader";
import { subscriptionService } from "@/services/subscription.service";

const STATUS_OPTIONS = [
  "ACTIVE",
  "PAST_DUE",
  "CANCELLED",
  "EXPIRED",
  "TRIAL",
] as const;

function SubscriptionDetailPage() {
  const params = useParams();
  const subscriptionId = params.id ?? "";
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-subscription", subscriptionId],
    queryFn: () => subscriptionService.getById(subscriptionId),
    enabled: Boolean(subscriptionId),
  });

  const { data: payments } = useQuery({
    queryKey: ["admin-subscription-payments", subscriptionId],
    queryFn: () => subscriptionService.listPayments(subscriptionId),
    enabled: Boolean(subscriptionId),
  });

  const { data: invoices } = useQuery({
    queryKey: ["admin-subscription-invoices", subscriptionId],
    queryFn: () => subscriptionService.listInvoices(subscriptionId),
    enabled: Boolean(subscriptionId),
  });

  const [status, setStatus] = useState<string>("");
  const [statusReason, setStatusReason] = useState<string>("");
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>("");
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundReason, setRefundReason] = useState<string>("");
  const [chargebackReason, setChargebackReason] = useState<string>("");
  const [chargebackStatus, setChargebackStatus] = useState<string>("reported");

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (nextStatus: string) =>
      subscriptionService.updateStatus(
        subscriptionId,
        nextStatus,
        statusReason.trim(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription", subscriptionId],
      });
      toast.success("Subscription updated");
      setStatusReason("");
    },
  });

  const { mutateAsync: refundPayment, isPending: isRefunding } = useMutation({
    mutationFn: () =>
      subscriptionService.refundPayment(selectedPaymentId, {
        amount: refundAmount ? Number(refundAmount) : undefined,
        reason: refundReason || undefined,
      }),
    onSuccess: () => {
      setRefundAmount("");
      setRefundReason("");
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription-payments", subscriptionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription-invoices", subscriptionId],
      });
      toast.success("Refund initiated");
    },
  });

  const { mutateAsync: markChargeback, isPending: isChargebackPending } =
    useMutation({
      mutationFn: () =>
        subscriptionService.markChargeback(selectedPaymentId, {
          reason: chargebackReason,
          status: chargebackStatus || undefined,
        }),
      onSuccess: () => {
        setChargebackReason("");
        queryClient.invalidateQueries({
          queryKey: ["admin-subscription-payments", subscriptionId],
        });
        toast.success("Chargeback recorded");
      },
    });

  const summary = useMemo(() => {
    if (!data) {
      return [];
    }
    return [
      { label: "Tenant", value: data.tenant.name },
      { label: "Plan", value: data.plan.name },
      { label: "Interval", value: data.interval },
      { label: "Amount", value: `₹${data.amount}` },
      { label: "Auto renew", value: data.autoRenew ? "Enabled" : "Manual" },
    ];
  }, [data]);

  const actionablePayments = payments?.data.filter((payment) =>
    ["CAPTURED", "PARTIALLY_REFUNDED"].includes(payment.status),
  );
  const selectedPayment = actionablePayments?.find(
    (payment) => payment.id === selectedPaymentId,
  );
  const refundableAmount = selectedPayment
    ? Number(selectedPayment.amount) - Number(selectedPayment.refundAmount ?? 0)
    : 0;
  const requestedRefundAmount = refundAmount ? Number(refundAmount) : undefined;
  const refundAmountValid =
    requestedRefundAmount === undefined ||
    (Number.isFinite(requestedRefundAmount) &&
      requestedRefundAmount > 0 &&
      requestedRefundAmount <= refundableAmount);

  if (isLoading) {
    return (
      <div className="text-sm text-slate-500">Loading subscription...</div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-500">Subscription not found.</p>
        <Link
          className="text-sm font-semibold text-slate-900"
          to="/subscriptions"
        >
          Back to subscriptions
        </Link>
      </div>
    );
  }

  const statusValue = status || data.status;

  return (
    <div className="space-y-8">
      <PageHeader
        title={`Subscription · ${data.plan.name}`}
        subtitle={`${data.tenant.name} · ${data.status}`}
        actions={
          <Link
            to="/subscriptions"
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
          >
            Back to subscriptions
          </Link>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summary.map((item) => (
          <div
            key={item.label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              {item.label}
            </p>
            <p className="mt-3 text-lg font-semibold text-slate-900">
              {item.value}
            </p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
            Status
          </h3>
          <div className="mt-4 space-y-3">
            <select
              value={statusValue}
              onChange={(event) => setStatus(event.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {statusValue !== data.status && (
              <input
                value={statusReason}
                onChange={(event) => setStatusReason(event.target.value)}
                placeholder="Required status-change reason (8+ characters)"
                className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
              />
            )}
            <button
              type="button"
              disabled={
                isPending ||
                statusValue === data.status ||
                statusReason.trim().length < 8
              }
              onClick={() => mutateAsync(statusValue)}
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
            >
              {isPending ? "Saving..." : "Update status"}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
            Renewal dates
          </h3>
          <div className="mt-4 space-y-2 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-900">Start:</span>{" "}
              {new Date(data.startDate).toLocaleDateString()}
            </p>
            <p>
              <span className="font-semibold text-slate-900">Renewal:</span>{" "}
              {data.renewalDate
                ? new Date(data.renewalDate).toLocaleDateString()
                : "—"}
            </p>
            <p>
              <span className="font-semibold text-slate-900">End:</span>{" "}
              {data.endDate ? new Date(data.endDate).toLocaleDateString() : "—"}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
          Payment history
        </h3>
        <div className="mt-4 space-y-3">
          {payments?.data.length ? (
            payments.data.map((payment) => (
              <div
                key={payment.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3 text-sm text-slate-600"
              >
                <div>
                  <p className="font-semibold text-slate-900">
                    ₹{payment.amount} · {payment.status}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(payment.createdAt).toLocaleString()}
                  </p>
                  {(payment.refundAmount || payment.chargebackStatus) && (
                    <p className="text-xs text-amber-600">
                      {payment.refundAmount
                        ? `Refund: ₹${payment.refundAmount}`
                        : null}
                      {payment.chargebackStatus
                        ? ` Chargeback: ${payment.chargebackStatus}`
                        : null}
                    </p>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {payment.gatewayPaymentId || payment.gatewayLinkId || "—"}
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">No payments recorded.</p>
          )}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
            Billing actions
          </h3>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Payment
              </label>
              <select
                value={selectedPaymentId}
                onChange={(event) => setSelectedPaymentId(event.target.value)}
                className="mt-2 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">Select captured payment</option>
                {actionablePayments?.map((payment) => (
                  <option key={payment.id} value={payment.id}>
                    ₹{payment.amount} · {payment.status} · {new Date(payment.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-xl border border-slate-100 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Refund
              </p>
              <div className="mt-3 space-y-3">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={refundableAmount || undefined}
                  value={refundAmount}
                  onChange={(event) => setRefundAmount(event.target.value)}
                  placeholder="Amount (leave blank for full refund)"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                />
                <input
                  value={refundReason}
                  onChange={(event) => setRefundReason(event.target.value)}
                  placeholder="Required refund reason (8+ characters)"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                />
                <button
                  type="button"
                  disabled={
                    !selectedPaymentId ||
                    isRefunding ||
                    refundReason.trim().length < 8 ||
                    !refundAmountValid
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        `Confirm ${refundAmount ? `₹${refundAmount}` : "full"} refund? This creates an external financial operation.`,
                      )
                    ) {
                      refundPayment();
                    }
                  }}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-50"
                >
                  {isRefunding ? "Processing..." : "Initiate refund"}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Chargeback
              </p>
              <div className="mt-3 space-y-3">
                <input
                  value={chargebackReason}
                  onChange={(event) => setChargebackReason(event.target.value)}
                  placeholder="Required chargeback reason (8+ characters)"
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                />
                <select
                  value={chargebackStatus}
                  onChange={(event) => setChargebackStatus(event.target.value)}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="reported">Reported</option>
                  <option value="under_review">Under review</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
                <button
                  type="button"
                  disabled={
                    !selectedPaymentId ||
                    chargebackReason.trim().length < 8 ||
                    isChargebackPending
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        "Record this chargeback state against the selected payment?",
                      )
                    ) {
                      markChargeback();
                    }
                  }}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700 disabled:opacity-50"
                >
                  {isChargebackPending ? "Saving..." : "Record chargeback"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
            Invoices
          </h3>
          <div className="mt-4 space-y-3">
            {invoices?.length ? (
              invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="rounded-xl border border-slate-100 px-4 py-3 text-sm text-slate-600"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {invoice.invoiceNumber}
                      </p>
                      <p className="text-xs text-slate-500">
                        {invoice.periodStart && invoice.periodEnd
                          ? `${new Date(invoice.periodStart).toLocaleDateString()} - ${new Date(invoice.periodEnd).toLocaleDateString()}`
                          : "Billing cycle"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        ₹{invoice.totalAmount}
                      </p>
                      <p className="text-xs text-slate-500">{invoice.status}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No invoices generated yet.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default SubscriptionDetailPage;
